
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const faqData = [
  {
    category: 'Orders & Shipping',
    items: [
      {
        question: 'How long does shipping take?',
        answer: 'Standard shipping typically takes 5-7 business days within the US. Express shipping is available for 2-3 business day delivery. International orders may take 10-14 business days depending on the destination.'
      },
      {
        question: 'How can I track my order?',
        answer: 'Once your order ships, you\'ll receive an email with a tracking number. You can use this number to track your package on our website or directly on the carrier\'s website.'
      },
      {
        question: 'Do you offer free shipping?',
        answer: 'Yes! We offer free standard shipping on all orders over $50. Orders under $50 have a flat shipping rate of $4.99.'
      },
      {
        question: 'Can I change or cancel my order?',
        answer: 'You can modify or cancel your order within 1 hour of placing it. After that, orders are processed quickly and cannot be changed. Please contact us immediately if you need assistance.'
      }
    ]
  },
  {
    category: 'Returns & Exchanges',
    items: [
      {
        question: 'What is your return policy?',
        answer: 'We accept returns within 3 days of delivery. Items must be unworn, unwashed, and have all original tags attached. Sale items are final sale and cannot be returned.'
      },
      {
        question: 'How do I start a return?',
        answer: 'To initiate a return, log into your account and go to your order history. Select the item you wish to return and follow the prompts. You\'ll receive a prepaid shipping label via email.'
      },
      {
        question: 'How long do refunds take?',
        answer: 'Once we receive your return, please allow 5-7 business days for inspection and processing. Refunds will be credited to your original payment method within 3-5 business days after approval.'
      },
      {
        question: 'Can I exchange an item for a different size?',
        answer: 'Yes! We offer free exchanges for different sizes. Simply start a return and select "Exchange" as your reason. We\'ll ship the new size as soon as we receive your return.'
      }
    ]
  },
  {
    category: 'Products & Sizing',
    items: [
      {
        question: 'How do I find my size?',
        answer: 'Each product page includes a detailed size guide with measurements. We recommend measuring yourself and comparing to our size chart for the best fit. If you\'re between sizes, we suggest sizing up.'
      },
      {
        question: 'Are your products true to size?',
        answer: 'Most of our items are true to size, but fit can vary by style. Check the product description for specific fit notes (e.g., "runs small" or "relaxed fit"). Customer reviews often include helpful sizing feedback.'
      },
      {
        question: 'What materials do you use?',
        answer: 'We use a variety of high-quality materials including cotton, polyester, rayon, and blends. Material composition is listed on each product page. We\'re committed to comfortable, durable fabrics.'
      },
      {
        question: 'How should I care for my items?',
        answer: 'Care instructions are included on each garment\'s label and product page. Generally, we recommend washing in cold water, using gentle cycle, and hanging to dry to maintain quality and fit.'
      }
    ]
  },
  {
    category: 'Payment & Security',
    items: [
      {
        question: 'What payment methods do you accept?',
        answer: 'We accept all major credit cards (Visa, Mastercard, American Express, Discover), PayPal, Apple Pay, and Google Pay. All transactions are securely processed.'
      },
      {
        question: 'Is my payment information secure?',
        answer: 'Absolutely! We use industry-standard SSL encryption to protect your personal and payment information. We never store your full credit card details on our servers.'
      },
      {
        question: 'Do you offer payment plans?',
        answer: 'Yes! We partner with Klarna and Afterpay to offer buy-now-pay-later options. You can split your purchase into 4 interest-free payments at checkout.'
      }
    ]
  }
];

export default function FAQPage() {
  const [openItems, setOpenItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');

  const toggleItem = (id) => {
    setOpenItems(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const filteredFAQ = activeCategory === 'all' 
    ? faqData 
    : faqData.filter(cat => cat.category === activeCategory);

  return (
    <div className="min-h-screen bg-white font-['Poppins']">
      <Navbar />
      
      <main className="pt-20">
        {/* Header */}
        <div className="bg-gradient-to-r from-cream-200 to-[#FCE7EF] py-12">
          <div className="max-w-[1200px] mx-auto px-6">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
              <Link to="/" className="hover:text-lilac-400 cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-lilac-400">FAQ</span>
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl font-bold text-gray-800">Frequently Asked Questions</h1>
            <p className="text-gray-600 mt-2">Find answers to common questions</p>
          </div>
        </div>

        <div className="max-w-[900px] mx-auto px-6 py-12">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-10 justify-center">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeCategory === 'all'
                  ? 'bg-lilac-400 text-white'
                  : 'bg-cream-200 text-gray-600 hover:bg-[#FCE7EF]'
              }`}
            >
              All Topics
            </button>
            {faqData.map(cat => (
              <button
                key={cat.category}
                onClick={() => setActiveCategory(cat.category)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeCategory === cat.category
                    ? 'bg-lilac-400 text-white'
                    : 'bg-cream-200 text-gray-600 hover:bg-[#FCE7EF]'
                }`}
              >
                {cat.category}
              </button>
            ))}
          </div>

          {/* FAQ Accordion */}
          <div className="space-y-8">
            {filteredFAQ.map((category) => (
              <div key={category.category}>
                <h2 className="font-['Playfair_Display'] text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="w-1.5 h-6 bg-lilac-400 rounded-full"></span>
                  {category.category}
                </h2>
                <div className="space-y-3">
                  {category.items.map((item, index) => {
                    const itemId = `${category.category}-${index}`;
                    const isOpen = openItems.includes(itemId);
                    
                    return (
                      <div 
                        key={itemId}
                        className="border border-gray-100 rounded-xl overflow-hidden bg-white"
                      >
                        <button
                          onClick={() => toggleItem(itemId)}
                          className="w-full px-5 py-4 flex items-center justify-between text-left cursor-pointer hover:bg-cream-200/50 transition-colors"
                        >
                          <span className="font-medium text-gray-800 pr-4">{item.question}</span>
                          <i className={`ri-arrow-down-s-line text-xl text-lilac-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
                        </button>
                        <div 
                          className={`overflow-hidden transition-all duration-300 ${
                            isOpen ? 'max-h-96' : 'max-h-0'
                          }`}
                        >
                          <p className="px-5 pb-4 text-gray-600 text-sm leading-relaxed">
                            {item.answer}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Still Need Help */}
          <div className="mt-16 text-center bg-cream-200 rounded-2xl p-10">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white text-lilac-400 mx-auto mb-4">
              <i className="ri-question-line text-3xl"></i>
            </div>
            <h3 className="font-['Playfair_Display'] text-2xl font-bold text-gray-800 mb-2">Still Have Questions?</h3>
            <p className="text-gray-600 mb-6">Can't find what you're looking for? We're here to help!</p>
            <Link 
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-lilac-400 text-white rounded-full font-medium hover:bg-lilac-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line"></i>
              Contact Us
            </Link>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
